import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { parse } from "date-fns";
import { enqueueSnackbar } from "notistack";
import {
  ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { CrsRule, ProxyRequest } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { queryKeys } from "~/api/queryKeys";
import { Icon } from "~/assets/icons/Icon";
import {
  DirectiveSeed,
  WafDirectiveModal,
} from "~/components/siteSettings/waf/WafDirectiveModal";
import { messageFromError } from "~/helpers/messageFromError";
import { tryCatch } from "~/helpers/tryCatch";
import { useCableSubscription } from "~/hooks/useCableSubscription";

import styles from "./RequestLog.module.scss";

// ── Constants ────────────────────────────────────────────────

// LEVELS is kept as a type-only union (not a runtime array) until the
// level filter UI is re-enabled — eslint flags it as unused otherwise.
type Level = "debug" | "info" | "warn" | "error";
const MAX_LOGS = 300;
// How many pixels from the edge of the scroll area trigger a fetch.
const SCROLL_TRIGGER_MARGIN = 400;
// Strict at-the-very-top threshold. Used to gate WebSocket-driven refreshes
// so we only fetch new logs when the user is actively watching the tail.
// Looser than SCROLL_TRIGGER_MARGIN — that one is for "near the edge,
// pre-fetch the next page", this one is for "the user is at the top".
const AT_TOP_THRESHOLD = 16;

const LEVEL_SHORT: Record<Level, string> = {
  debug: "DBG",
  info: "INF",
  warn: "WRN",
  error: "ERR",
};

const LEVEL_CLASS: Record<Level, string> = {
  debug: styles.tokenLevelDbg,
  info: styles.tokenLevelInf,
  warn: styles.tokenLevelWrn,
  error: styles.tokenLevelErr,
};

type RansackQueryType = NonNullable<
  NonNullable<Parameters<typeof apiClient.sites.proxyRequestsDetail>[1]>["q"]
>;

// ── Searchable fields ────────────────────────────────────────

// The base ransack attribute (without predicate suffix), e.g. "trace", "client_ip"
type RansackAttr =
  | "trace"
  | "client_ip"
  | "status"
  | "uri"
  | "verb"
  | "kind"
  | "bot"
  | "cache"
  | "err"
  | "port"
  | "region"
  | "created_at"
  | "waf_rule"
  | "waf_directive"
  | "waf_action"
  | "guard_action"
  | "guard_serial";

type SearchFieldDef = {
  token: string;
  attr: RansackAttr;
  label: string;
  group?: string;
  /** Override predicate suffix. If set, the value is taken as-is and glob
   * detection is skipped. Used for date range fields where we want gteq/lteq
   * regardless of the value. */
  predicate?: string;
  /** When true, the field is treated as an integer column. Range syntax
   * "400..499" translates to gteq + lteq, bare integers translate to
   * exact match. Wildcards (e.g. "5*") are NOT supported on numeric
   * fields — ransack's _start / _cont predicates rely on SQL LIKE,
   * which doesn't work on integer columns. */
  numeric?: boolean;
  /** When set to "date", the value is parsed leniently (multiple
   * supported formats — ISO, US slash, EU slash, with or without time)
   * and re-serialised as the local-time ISO form the backend expects.
   * Used by the from/to range tokens. */
  valueType?: "date";
};

const SEARCH_FIELDS: SearchFieldDef[] = [
  { token: "ref", attr: "trace", label: "Reference ID" },
  { token: "ip", attr: "client_ip", label: "IP" },
  { token: "port", attr: "port", label: "Port" },
  { token: "region", attr: "region", label: "Region" },
  { token: "status", attr: "status", label: "Status", numeric: true },
  { token: "uri", attr: "uri", label: "URI" },
  { token: "method", attr: "verb", label: "Method" },
  { token: "kind", attr: "kind", label: "Kind" },
  { token: "bot", attr: "bot", label: "Bot" },
  { token: "cache", attr: "cache", label: "Cache" },
  { token: "error", attr: "err", label: "Error" },
  {
    token: "from",
    attr: "created_at",
    label: "From",
    predicate: "gteq",
    group: "Date",
    valueType: "date",
  },
  {
    token: "to",
    attr: "created_at",
    label: "To",
    predicate: "lteq",
    group: "Date",
    valueType: "date",
  },
  {
    token: "rule",
    attr: "waf_rule",
    label: "Rule",
    group: "WAF",
    numeric: true,
  },
  {
    token: "directive",
    attr: "waf_directive",
    label: "Directive",
    group: "WAF",
    numeric: true,
  },
  { token: "waf", attr: "waf_action", label: "Action", group: "WAF" },
  { token: "guard", attr: "guard_action", label: "Action", group: "Guard" },
  {
    token: "serial",
    attr: "guard_serial",
    label: "Serial",
    group: "Guard",
    numeric: true,
  },
];

const TOKEN_TO_FIELD: Record<string, SearchFieldDef> = Object.fromEntries(
  SEARCH_FIELDS.map((f) => [f.token, f]),
);

// Convert a value with optional glob markers to a ransack key + cleaned value
// "*foo"  → end (ends with)
// "foo*"  → start (starts with)
// "*foo*" → cont (contains)
// "foo"   → eq (exact)
function resolvePredicate(value: string): { suffix: string; cleaned: string } {
  const startsWithGlob = value.startsWith("*");
  const endsWithGlob = value.endsWith("*") && value.length > 1;

  if (startsWithGlob && endsWithGlob) {
    return { suffix: "cont", cleaned: value.slice(1, -1) };
  }
  if (startsWithGlob) {
    return { suffix: "end", cleaned: value.slice(1) };
  }
  if (endsWithGlob) {
    return { suffix: "start", cleaned: value.slice(0, -1) };
  }

  return { suffix: "eq", cleaned: value };
}

// ── Lexer ────────────────────────────────────────────────────
//
// The lexer turns the raw search string into a stream of typed tokens
// for the parser below. Three syntactic elements are recognised beyond
// the existing key:value tokens:
//   - LPAREN  "("  — open a group
//   - RPAREN  ")"  — close a group
//   - OR      "or" / "OR" (case-insensitive, only when not part of a key:value)
// Adjacent TOKENs are still implicitly AND-ed (no AND keyword needed,
// though "and"/"AND" is silently accepted to keep mixed habits working).
//
// Parens are value terminators in addition to whitespace, so `rule:0..)`
// correctly splits the value `0..` from the closing paren. Quoted values
// pass parens through literally, so `error:"foo (bar)"` still works.

type LexToken =
  | {
      type: "TOKEN";
      key: string;
      value: string;
      /** Character range in the original input — used by upsertSearchToken
       * to splice replacement tokens back in without losing surrounding text. */
      start: number;
      end: number;
    }
  | { type: "LPAREN"; start: number; end: number }
  | { type: "RPAREN"; start: number; end: number }
  | { type: "OR"; start: number; end: number }
  | { type: "NOT"; start: number; end: number };

// Backwards-compat alias used by upsertSearchToken — it only cares about
// key:value pairs and ignores parens / OR keywords.
type ParsedToken = {
  key: string;
  value: string;
  start: number;
  end: number;
};

function lexQuery(input: string): LexToken[] {
  const out: LexToken[] = [];
  let i = 0;

  const isWs = (c: string) => /\s/.test(c);

  while (i < input.length) {
    while (i < input.length && isWs(input[i])) i++;
    if (i >= input.length) break;

    const start = i;

    if (input[i] === "(") {
      out.push({ type: "LPAREN", start, end: i + 1 });
      i++;
      continue;
    }
    if (input[i] === ")") {
      out.push({ type: "RPAREN", start, end: i + 1 });
      i++;
      continue;
    }

    // Read identifier up to ':', whitespace, or paren
    const idStart = i;
    while (
      i < input.length &&
      input[i] !== ":" &&
      input[i] !== "(" &&
      input[i] !== ")" &&
      !isWs(input[i])
    ) {
      i++;
    }

    if (i >= input.length || input[i] !== ":") {
      // Bare word — recognise OR/AND/NOT keywords; silently drop anything else.
      const word = input.slice(idStart, i).toLowerCase();
      if (word === "or") {
        out.push({ type: "OR", start, end: i });
      } else if (word === "not") {
        out.push({ type: "NOT", start, end: i });
      }
      // "and" is accepted but no-op (adjacent tokens are AND by default)
      // Other bare words are dropped, matching the previous tokenizer's behaviour.
      continue;
    }

    const key = input.slice(idStart, i).toLowerCase();
    i++; // consume ':'

    // Read value: quoted (consumes parens literally) or bare (terminates on whitespace/parens)
    let value = "";
    if (input[i] === '"') {
      i++; // consume opening quote
      while (i < input.length && input[i] !== '"') {
        if (input[i] === "\\" && i + 1 < input.length) {
          value += input[i + 1];
          i += 2;
        } else {
          value += input[i];
          i++;
        }
      }
      if (i < input.length && input[i] === '"') i++; // consume closing quote
    } else {
      while (
        i < input.length &&
        !isWs(input[i]) &&
        input[i] !== "(" &&
        input[i] !== ")"
      ) {
        value += input[i];
        i++;
      }
    }

    out.push({ type: "TOKEN", key, value, start, end: i });
  }

  return out;
}

// upsertSearchToken only needs the key:value pairs, ignoring parens / OR.
// Kept as a separate function for the click-to-filter flows.
function tokenizeSearchInput(input: string): ParsedToken[] {
  return lexQuery(input)
    .filter(
      (t): t is Extract<LexToken, { type: "TOKEN" }> => t.type === "TOKEN",
    )
    .map(({ key, value, start, end }) => ({ key, value, start, end }));
}

// Format a value for the search input. Wraps in quotes (and escapes inner
// quotes / backslashes) when the value contains characters that would
// otherwise break tokenization (whitespace, colons, quotes).
function formatSearchValue(value: string): string {
  if (/[\s:"\\]/.test(value)) {
    return `"${value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
  }

  return value;
}

function formatSearchToken(key: string, value: string): string {
  return `${key}:${formatSearchValue(value)}`;
}

// ── AST ──────────────────────────────────────────────────────
//
// Parsed query is a tree of Group + Condition nodes. A Condition is one
// ransack predicate (e.g. status_gteq=500); a Group is an AND/OR of
// child nodes. Adjacent key:value tokens form an implicit AND group;
// the OR keyword and parens introduce explicit nesting.

type Condition = {
  type: "cond";
  attr: string; // ransack attribute name (e.g. "status", "waf_rule")
  predicate: string; // "eq", "gteq", "lteq", "gt", "lt", "cont", "start", "end"
  value: string;
};

type Group = {
  type: "group";
  op: "and" | "or";
  children: AstNode[];
};

type AstNode = Group | Condition;

// Format strings tried in order when parsing a `from:` / `to:` value.
// First match wins. Covers ISO 8601 (with T or space separator), US
// slash (M/d/yyyy, 24h or 12h AM/PM), and EU slash (d/M/yyyy) — each
// with and without time. The single-digit `M` / `d` / `H` / `h` / `m`
// / `s` patterns also accept zero-padded variants, so "01/02/2026" and
// "1/2/2026" both parse via the same format string. date-fns `parse`
// is strict — it requires the entire input to match the format — so
// the more specific (longer) variants are listed first.
const DATE_FORMATS = [
  // ISO with time
  "yyyy-MM-dd'T'HH:mm:ss",
  "yyyy-MM-dd'T'HH:mm",
  // ISO with space (common variant)
  "yyyy-MM-dd HH:mm:ss",
  "yyyy-MM-dd HH:mm",
  // ISO date only
  "yyyy-MM-dd",
  // US slash, 24-hour
  "M/d/yyyy H:mm:ss",
  "M/d/yyyy H:mm",
  // US slash, 12-hour AM/PM
  "M/d/yyyy h:mm:ss a",
  "M/d/yyyy h:mm a",
  // US slash, date only
  "M/d/yyyy",
  // EU slash fallback. Ambiguous with US when day/month ≤12 — US wins.
  "d/M/yyyy H:mm",
  "d/M/yyyy",
] as const;

// Detects an explicit timezone designator at the end of a date string —
// a trailing "Z" or a numeric offset like "+02:00" / "-0500".
const HAS_TZ_DESIGNATOR = /(?:Z|[+-]\d{2}:?\d{2})$/i;

// Parse a flexible date string into an absolute instant.
//
// The log list renders `created_at` in UTC (the raw ISO strings end in
// "Z"), so that's the reference frame the user reads timestamps off of.
// To make a value round-trip back to the log it came from:
//   - inputs WITH a zone designator ("...Z", "...+02:00") are taken as the
//     absolute instant they denote.
//   - inputs WITHOUT one ("2026-06-13T16:05:10", "2026-06-13") are read as
//     UTC wall-clock — NOT browser-local — so typing the time shown in a
//     log line filters to that exact moment regardless of the user's tz.
function parseFlexibleDate(value: string): Date | null {
  const trimmed = value.trim();

  if (HAS_TZ_DESIGNATOR.test(trimmed)) {
    const native = new Date(trimmed);

    return isNaN(native.getTime()) ? null : native;
  }

  for (const fmt of DATE_FORMATS) {
    const parsed = parse(trimmed, fmt, new Date());
    if (!isNaN(parsed.getTime())) {
      // date-fns parses into a browser-local Date; reinterpret those same
      // wall-clock fields as UTC so the instant matches the UTC log times.
      return new Date(
        Date.UTC(
          parsed.getFullYear(),
          parsed.getMonth(),
          parsed.getDate(),
          parsed.getHours(),
          parsed.getMinutes(),
          parsed.getSeconds(),
        ),
      );
    }
  }

  // Last-resort fallback: hand it to the native Date constructor for
  // anything we didn't explicitly enumerate (some browsers accept
  // additional ISO variants, RFC 2822 strings, etc.).
  const native = new Date(trimmed);

  return isNaN(native.getTime()) ? null : native;
}

// Re-serialise to a UTC ISO 8601 timestamp — the same frame the API returns
// `created_at` in and compares the gteq/lteq bounds against. Millisecond
// precision is kept (the log timestamps carry it, e.g. ...:27.163Z) so a
// `to:`/lteq bound built from a clicked row still includes that exact row;
// dropping to whole seconds would round the bound *below* the record and
// exclude it. Zero milliseconds are trimmed to keep typed date queries tidy.
function formatUtcDateTime(date: Date): string {
  return date.toISOString().replace(/\.000Z$/, "Z");
}

// Convert one parsed key:value token into one or more conditions.
// A single token can produce multiple conditions (e.g. status:500..599
// becomes status_gteq=500 + status_lteq=599). Returns [] if the token
// can't be mapped to any predicate (unknown field, malformed value, etc.).
function tokenToConditions(key: string, value: string): Condition[] {
  if (!value) return [];
  const field = TOKEN_TO_FIELD[key];
  if (!field) return [];

  if (field.predicate) {
    let serialised = value;

    if (field.valueType === "date") {
      const parsed = parseFlexibleDate(value);
      if (!parsed) return [];
      serialised = formatUtcDateTime(parsed);
    }

    return [
      {
        type: "cond",
        attr: field.attr,
        predicate: field.predicate,
        value: serialised,
      },
    ];
  }

  if (field.numeric) {
    const range = parseNumericRange(value);

    if (range) {
      const out: Condition[] = [];
      if (range.lower != null) {
        out.push({
          type: "cond",
          attr: field.attr,
          predicate: "gteq",
          value: String(range.lower),
        });
      }
      if (range.upper != null) {
        out.push({
          type: "cond",
          attr: field.attr,
          predicate: "lteq",
          value: String(range.upper),
        });
      }

      return out;
    }
    if (/^\d+$/.test(value)) {
      return [{ type: "cond", attr: field.attr, predicate: "eq", value }];
    }

    // Wildcards / non-numeric on integer columns are silently dropped —
    // ransack's _start / _cont rely on SQL LIKE which doesn't work on ints.
    return [];
  }

  const { suffix, cleaned } = resolvePredicate(value);
  if (!cleaned) return [];

  return [
    { type: "cond", attr: field.attr, predicate: suffix, value: cleaned },
  ];
}

// Predicate negation table. Used by negate() to push NOT down to
// individual conditions via De Morgan's, so NOT compiles to the same
// flat ransack predicates we already emit (no special server-side
// `not_and` / `not_or` group operator needed — ransack doesn't have one).
const NEGATED_PREDICATE: Record<string, string> = {
  eq: "not_eq",
  not_eq: "eq",
  cont: "not_cont",
  not_cont: "cont",
  start: "not_start",
  not_start: "start",
  end: "not_end",
  not_end: "end",
  in: "not_in",
  not_in: "in",
  gt: "lteq",
  lteq: "gt",
  gteq: "lt",
  lt: "gteq",
};

// Push NOT down through a sub-tree, applying De Morgan's law:
//   not (a AND b)  →  (not a) OR  (not b)
//   not (a OR  b)  →  (not a) AND (not b)
//   not cond       →  cond with the predicate flipped via NEGATED_PREDICATE
//
// Returning the node unchanged when the predicate has no negation in
// the table would silently flip the user's intent, so we drop it
// instead — matches the lexer's behaviour for unrecognised tokens.
function negate(node: AstNode): AstNode | null {
  if (node.type === "cond") {
    const flipped = NEGATED_PREDICATE[node.predicate];
    if (!flipped) return null;

    return { ...node, predicate: flipped };
  }

  const negatedChildren = node.children
    .map(negate)
    .filter((c): c is AstNode => c !== null);

  if (negatedChildren.length === 0) return null;
  if (negatedChildren.length === 1) return negatedChildren[0];

  return {
    type: "group",
    op: node.op === "and" ? "or" : "and",
    children: negatedChildren,
  };
}

// ── Parser ───────────────────────────────────────────────────
//
// Recursive-descent parser with SQL-style precedence: NOT binds
// tightest, then AND, then OR. Parens override. The grammar:
//
//   query    := orExpr
//   orExpr   := andExpr (OR andExpr)*
//   andExpr  := notExpr notExpr*            // adjacent atoms = AND
//   notExpr  := "not" notExpr | atom
//   atom     := "(" orExpr ")" | TOKEN
//
// Returns null for an empty / all-malformed input.

function parseSearchInput(input: string): AstNode | null {
  const tokens = lexQuery(input);
  let pos = 0;

  const peek = () => tokens[pos];
  const advance = () => tokens[pos++];

  // Build a group, collapsing one-child groups into the child itself
  // and flattening nested same-op groups (so `a or b or c` is one OR
  // group of three children, not OR(a, OR(b, c))).
  const group = (op: "and" | "or", children: AstNode[]): AstNode | null => {
    const flat: AstNode[] = [];
    for (const c of children) {
      if (c.type === "group" && c.op === op) {
        flat.push(...c.children);
      } else {
        flat.push(c);
      }
    }
    if (flat.length === 0) return null;
    if (flat.length === 1) return flat[0];

    return { type: "group", op, children: flat };
  };

  const parseAtom = (): AstNode | null => {
    const t = peek();
    if (!t) return null;

    if (t.type === "LPAREN") {
      advance();
      const inner = parseOr();
      // Tolerate missing ) — just consume it if present.
      if (peek()?.type === "RPAREN") advance();

      return inner;
    }

    if (t.type === "TOKEN") {
      advance();
      const conds = tokenToConditions(t.key, t.value);
      if (conds.length === 0) return null;
      if (conds.length === 1) return conds[0];

      return { type: "group", op: "and", children: conds };
    }

    // Stray RPAREN or OR — caller handles termination, just stop here.
    return null;
  };

  const parseNot = (): AstNode | null => {
    if (peek()?.type === "NOT") {
      advance();
      // Recurse so `not not x` collapses cleanly via the negate-of-negate
      // table entries (e.g. eq → not_eq → eq).
      const inner = parseNot();
      if (!inner) return null;

      return negate(inner);
    }

    return parseAtom();
  };

  const parseAnd = (): AstNode | null => {
    const items: AstNode[] = [];

    while (true) {
      const t = peek();
      if (!t) break;
      if (t.type === "RPAREN" || t.type === "OR") break;

      const node = parseNot();
      if (node) {
        items.push(node);
      } else if (t === peek()) {
        // parseNot didn't consume anything (unknown token / unmappable).
        // Skip the offending token to avoid an infinite loop.
        advance();
      }
    }

    return group("and", items);
  };

  const parseOr = (): AstNode | null => {
    const items: AstNode[] = [];
    const first = parseAnd();
    if (first) items.push(first);

    while (peek()?.type === "OR") {
      advance();
      const next = parseAnd();
      if (next) items.push(next);
    }

    return group("or", items);
  };

  return parseOr();
}

// ── Ransack serializer ───────────────────────────────────────
//
// astToRansack walks the AST and emits the params object that the
// proxyRequestsDetail endpoint accepts. Two output shapes:
//
//   1. Fast path — a "simple flat AND" (top-level AND group with only
//      Conditions, or just a single Condition) emits the existing flat
//      `attr_predicate: value` form. This preserves the API contract
//      for the 95% of queries that don't use OR or parens.
//
//   2. Slow path — anything with OR or nested groups emits the ransack
//      `g[]` grouping format as a nested object structure. The qs
//      brackets serializer turns this into `q[g][][m]=or` etc.
//
// IMPORTANT: every subgroup wrapper (including the trivial wrapper
// around a single condition) must include an `m` key as its FIRST
// entry. Rack's `parse_nested_query` starts a new element of `a[]`
// only when it encounters a key already present in the current
// element. Without the `m` sentinel, sibling subgroups with disjoint
// keys (e.g. `{err_eq: ...}` next to `{m: "or", g: [...]}`) get
// merged into a single hash on decode, silently dropping the second
// subgroup's structure. The `m` sentinel is also harmless on a
// single-condition subgroup — ransack just treats "and"/"or" as a
// no-op when there's only one condition.

interface RansackParams {
  [key: string]: string | string[] | RansackParams | RansackParams[];
}

function isSimpleFlatAnd(ast: AstNode): boolean {
  if (ast.type === "cond") return true;
  if (ast.op !== "and") return false;
  if (!ast.children.every((c) => c.type === "cond")) return false;

  // Reject if two children share an `attr_predicate` key — emitFlat would
  // collapse them into a single object key and silently lose the second.
  // Common after De Morgan's: e.g. `not (status:502 or status:503)` becomes
  // an AND of two `status_not_eq` conditions. The slow path emits each
  // condition in its own subgroup, so the duplication survives the trip.
  const seen = new Set<string>();
  for (const c of ast.children as Condition[]) {
    const key = `${c.attr}_${c.predicate}`;
    if (seen.has(key)) return false;
    seen.add(key);
  }

  return true;
}

function emitFlat(ast: AstNode): RansackParams {
  const out: RansackParams = {};
  const conds = ast.type === "cond" ? [ast] : (ast.children as Condition[]);

  for (const c of conds) {
    out[`${c.attr}_${c.predicate}`] = c.value;
  }

  return out;
}

function emitGroup(node: AstNode): RansackParams {
  // Always normalise into a group for the nested form.
  const grp: Group =
    node.type === "group"
      ? node
      : { type: "group", op: "and", children: [node] };

  const out: RansackParams = { m: grp.op };
  const subgroups: RansackParams[] = [];

  for (const child of grp.children) {
    if (child.type === "group") {
      subgroups.push(emitGroup(child));
    } else {
      // Each condition goes into its own sub-group rather than being
      // flattened onto the parent. This prevents key collisions when
      // multiple conditions share the same attr_predicate — e.g.
      // "status:401 or status:403" would produce two `status_eq` keys,
      // and the second would silently overwrite the first. The `m`
      // sentinel is required (see the IMPORTANT note above): it
      // guarantees a key collision against the next sibling, which is
      // what tells Rack to start a new array element instead of
      // merging the two subgroups into one hash.
      subgroups.push({
        m: "and",
        [`${child.attr}_${child.predicate}`]: child.value,
      });
    }
  }

  if (subgroups.length > 0) {
    out.g = subgroups;
  }

  return out;
}

function astToRansack(ast: AstNode): RansackParams {
  if (isSimpleFlatAnd(ast)) {
    return emitFlat(ast);
  }

  return { g: [emitGroup(ast)] };
}

// Convert a numeric range literal into bounds. Supports closed ranges
// ("400..499") and open-ended ranges ("0.." → gteq only, "..100" → lteq
// only). Returns null if the value isn't a recognised range form or if
// the closed range has lower > upper.
function parseNumericRange(
  value: string,
): { lower?: number; upper?: number } | null {
  const closed = value.match(/^(\d+)\.\.(\d+)$/);

  if (closed) {
    const lower = parseInt(closed[1], 10);
    const upper = parseInt(closed[2], 10);

    if (lower > upper) return null;

    return { lower, upper };
  }

  const lowerOnly = value.match(/^(\d+)\.\.$/);

  if (lowerOnly) {
    return { lower: parseInt(lowerOnly[1], 10) };
  }

  const upperOnly = value.match(/^\.\.(\d+)$/);

  if (upperOnly) {
    return { upper: parseInt(upperOnly[1], 10) };
  }

  return null;
}

// Add or replace a key:value token in the search input. Preserves any
// surrounding tokens and uses the tokenizer (so quoted values are handled
// correctly).
function upsertSearchToken(input: string, token: string, value: string) {
  const tokens = tokenizeSearchInput(input);
  const formatted = formatSearchToken(token, value);
  const existing = tokens.find((t) => t.key === token.toLowerCase());

  if (existing) {
    const before = input.slice(0, existing.start);
    const after = input.slice(existing.end);

    return `${before}${formatted}${after}`;
  }

  const trimmed = input.trim();

  return trimmed ? `${trimmed} ${formatted}` : formatted;
}

// ── Helpers ──────────────────────────────────────────────────

function statusTokenClass(status: number) {
  if (status >= 500) return styles.tokenStatus5xx;
  if (status >= 400) return styles.tokenStatus4xx;
  if (status >= 300) return styles.tokenStatus3xx;
  if (status >= 200) return styles.tokenStatus2xx;

  return styles.tokenValue;
}

function formatDuration(ms: number) {
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;

  return `${Math.round(ms)}ms`;
}

// Filter out logs that are already present in `existing`. Used to dedupe
// when merging incoming responses, since concurrent fetches (e.g. fetchInitial
// racing with WS-triggered fetchNewLogs, or overlapping WS prepends) can pull
// the same records from the API.
function dedupeAgainst(
  existing: ProxyRequest[],
  incoming: ProxyRequest[],
): ProxyRequest[] {
  const ids = new Set(existing.map((l) => l.id));

  return incoming.filter((l) => !ids.has(l.id));
}

// ── Formatted log line ───────────────────────────────────────

type TokenSpec = {
  key: string;
  val: string;
  cls?: string;
  filterToken?: string;
  filterValue?: string;
};

type LogTokensProps = {
  log: ProxyRequest;
  onFilter: (token: string, value: string) => void;
};

function LogTokens({ log, onFilter }: LogTokensProps) {
  const pairs: TokenSpec[] = [];

  if (log.region)
    pairs.push({
      key: "r",
      val: log.region,
      filterToken: "region",
      filterValue: log.region,
    });
  if (log.status != null)
    pairs.push({
      key: "s",
      val: String(log.status),
      cls: statusTokenClass(log.status),
      filterToken: "status",
      filterValue: String(log.status),
    });
  if (log.port != null)
    pairs.push({
      key: "p",
      val: String(log.port),
      filterToken: "port",
      filterValue: String(log.port),
    });
  if (log.verb)
    pairs.push({
      key: "m",
      val: log.verb.toUpperCase(),
      filterToken: "method",
      filterValue: log.verb,
    });
  if (log.bot != null)
    pairs.push({
      key: "b",
      val: String(log.bot),
      filterToken: "bot",
      filterValue: String(log.bot),
    });
  if (log.client_ip)
    pairs.push({
      key: "ip",
      val: log.client_ip,
      filterToken: "ip",
      filterValue: log.client_ip,
    });
  if (log.cache)
    pairs.push({
      key: "c",
      val: log.cache,
      filterToken: "cache",
      filterValue: log.cache,
    });
  if (log.duration != null)
    pairs.push({ key: "d", val: formatDuration(log.duration) });
  if (log.trace)
    pairs.push({
      key: "t",
      val: log.trace,
      filterToken: "ref",
      filterValue: log.trace,
    });
  if (log.uri)
    pairs.push({
      key: "u",
      val: log.uri,
      filterToken: "uri",
      filterValue: log.uri,
    });
  if (log.err)
    pairs.push({
      key: "err",
      val: log.err,
      filterToken: "error",
      filterValue: log.err,
    });

  const handleTokenClick = (
    e: React.MouseEvent,
    token: string,
    value: string,
  ) => {
    e.stopPropagation();
    onFilter(token, value);
  };

  return (
    <>
      <span className={styles.tokenTimestamp}>{log.created_at} </span>
      <span className={LEVEL_CLASS[log.level]}>{LEVEL_SHORT[log.level]} </span>
      <span className={styles.tokenMessage}>{log.message} </span>
      {pairs.map(({ key, val, cls, filterToken, filterValue }) => (
        <span key={key}>
          <span className={styles.tokenKey}>{key}=</span>
          {filterToken && filterValue ? (
            <span
              role="button"
              tabIndex={0}
              className={clsx(cls ?? styles.tokenValue, styles.tokenClickable)}
              onClick={(e) => handleTokenClick(e, filterToken, filterValue)}
              title={`Filter by ${filterToken}`}
            >
              {val}
            </span>
          ) : (
            <span className={cls ?? styles.tokenValue}>{val}</span>
          )}{" "}
        </span>
      ))}
    </>
  );
}

// ── Component ────────────────────────────────────────────────

type RequestLogProps = {
  siteId: number;
  onConnectionChange?: (connected: boolean) => void;
  /** Pre-populated search value, e.g. when deep-linked from a chart click. */
  initialSearch?: string;
};

// A FetchTask describes a single unit of fetch work. The reducer-style
// effect below cancels any in-flight fetch and runs the latest task, so
// there is always exactly one request in flight at a time.
type FetchTask = {
  // Unique id so identical task shapes (e.g. two refreshes) still re-trigger
  // the effect.
  id: number;
  // How to merge the response into the existing logs.
  mode: "replace" | "older" | "newer";
  // Cursor IDs (only used for older/newer).
  after?: string;
  before?: string;
  // For "newer" tasks: when true, the prepended logs scroll the view to
  // the top instead of preserving the user's scroll position. Used by
  // WebSocket-driven refreshes and the "scroll back to top" recovery —
  // both cases where the user wants to see the freshly arrived data.
  // Trim-recovery fetchNewer (loading back logs that were trimmed off the
  // top of the buffer during scroll-down) keeps position-preservation.
  liveTail?: boolean;
  // The filter snapshot at the time the task was scheduled. The fetch
  // effect uses this so the response always matches the filter that
  // produced it (no stale-data flashes).
  filters: { levels: Level[]; search: string };
};

const RequestLog = ({
  siteId,
  onConnectionChange,
  initialSearch = "",
}: RequestLogProps) => {
  const [logs, setLogs] = useState<ProxyRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasOlder, setHasOlder] = useState(true);
  const [hasNewer, setHasNewer] = useState(false);
  // Setter is intentionally not destructured — the level filter UI is
  // hidden until warn/error logs ship. The state is kept (always an
  // empty Set) so scheduleTask's filter snapshot keeps the right shape
  // and we don't have to thread a temporary `levels: []` through
  // buildRansack.
  const [levelFilters] = useState<Set<Level>>(new Set());
  const [searchValue, setSearchValue] = useState(initialSearch);
  const [searchDraft, setSearchDraft] = useState(initialSearch);
  const [searchFieldOpen, setSearchFieldOpen] = useState(false);
  const [searchHelpOpen, setSearchHelpOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // The single fetch task being executed. Setting this triggers the fetch
  // effect; any previous in-flight fetch is aborted via AbortController.
  const [fetchTask, setFetchTask] = useState<FetchTask | null>(null);

  // Directive modal: when set, the WafDirectiveModal opens with the seed
  // pre-filled (rule + method + path captured from a triggered WAF match).
  // null when closed. Reuses the same modal as the WAF settings page.
  const [directiveSeed, setDirectiveSeed] = useState<DirectiveSeed | null>(
    null,
  );

  // CRS rules — used (1) to render the rule message in the WAF Matches
  // tooltip and (2) as the modal's `availableRules` source. Same query
  // key as WafBreakdownTable / WafSettings so a single network round-trip
  // serves all three. Always enabled here because the data is small,
  // 10-min stale, and we need the lookup ready by the time the user
  // expands a log entry.
  const { data: crsData } = useQuery({
    queryKey: queryKeys.crsRules(),
    queryFn: () => apiClient.crsRules.crsRulesList(),
    staleTime: 10 * 60 * 1000,
  });
  const crsRules = useMemo(() => crsData?.data.crs_rules ?? [], [crsData]);
  const crsRulesById = useMemo(() => {
    const map = new Map<number, CrsRule>();
    crsRules.forEach((r) => map.set(r.id, r));

    return map;
  }, [crsRules]);
  const crsGroupIds = useMemo(
    () =>
      Array.from(new Set(crsRules.map((r) => r.group))).sort((a, b) => a - b),
    [crsRules],
  );

  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchFieldDropdownRef = useRef<HTMLDivElement>(null);
  const searchHelpRef = useRef<HTMLDivElement>(null);

  const logsRef = useRef(logs);
  const scrollRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const bottomSentinelRef = useRef<HTMLDivElement>(null);
  const taskIdRef = useRef(0);
  // Scroll-restore anchor. Set immediately before a prepend ("newer")
  // or append ("older") commit. The scroll-restore layoutEffect finds
  // the anchor element in the rendered DOM, measures its new viewport-
  // relative position, and adjusts scrollTop so the position matches
  // what was captured here. This is independent of total scrollHeight
  // changes, which is necessary because the same setLogs commit also
  // trims from the opposite end to keep the buffer ≤ MAX_LOGS — so
  // height-delta math conflates "added at this end" with "removed at
  // the other end", and a delta-based scrollTop adjustment is wrong.
  const anchorIdRef = useRef<string | null>(null);
  const anchorViewportTopRef = useRef(0);
  const lastScrollTopRef = useRef(0);
  // Snapshot the has-more flags so the scroll handler can read the latest
  // values without being recreated on every state change.
  const hasOlderRef = useRef(true);
  const hasNewerRef = useRef(false);
  // Set when a scroll-driven fetch (older/newer) is scheduled, cleared in
  // the run function after the response has been applied (success, empty,
  // dedupe, or error). For upward fetches we defer the clear to the next
  // animation frame so the layout-effect scroll restore has run first.
  const scrollFetchInFlightRef = useRef(false);
  // Holds the AbortController of the in-flight fetch so the scroll
  // handler can abort it directly when the user moves to the opposite
  // end of the buffer (e.g., started a fetchOlder near the bottom, then
  // scrolled to the top — we no longer want those older logs and the
  // user shouldn't have to wait for them to land before a fetchNewer
  // can fire).
  const currentControllerRef = useRef<AbortController | null>(null);
  // True iff the user is parked at the top of the scroll container. WS
  // refreshes only fire when this is true; otherwise we mark
  // isDirtyRef and wait for the user to scroll back up.
  const isAtTopRef = useRef(true);
  // "There are new logs we haven't fetched yet." Set by the WebSocket
  // "new-logs" handler (and the connected handler when the user is
  // scrolled down). Cleared by flushDirty() at the moment it decides to
  // fetch. flushDirty is called on three trigger points:
  //   1. Arrival of a new-logs WS signal (immediate attempt)
  //   2. Completion of any in-flight fetch (drain the backlog)
  //   3. Scroll-back-to-top transition
  // It's also gated on isAtTopRef and inFlightModeRef, so the dirty flag
  // serves as a single source of "work to do" that persists until one
  // of those gates opens.
  const isDirtyRef = useRef(false);
  // Set when the next setLogs commit should snap the view to scrollTop=0
  // instead of preserving the user's scroll position. Two cases:
  //   - a live-tail prepend (WS-driven or scroll-back-to-top recovery), so
  //     freshly arrived logs land in view at the top, and
  //   - a `replace` (filter/search change, WS reconnect, fallback), so the
  //     user lands on the newest matching rows from the top rather than at a
  //     stale offset — which, on a short result set, would also drop them in
  //     the bottom trigger zone and fire a spurious `older` fetch.
  const snapToTopRef = useRef(false);
  // Mirror of fetchTask state, kept in sync via useLayoutEffect. Used by
  // the 3s fallback effect to check whether a task is already in flight
  // — the fallback's deps are intentionally empty (`[]`), so it would
  // otherwise close over the initial `fetchTask = null` and fire even
  // when a task IS in progress, double-cancelling the request.
  const fetchTaskRef = useRef<FetchTask | null>(null);
  // Mode of the fetch currently in flight (null when idle). flushDirty
  // reads this to defer WS-driven `newer` fetches while ANY fetch is
  // in flight — otherwise a new `newer` task would abort the in-flight
  // request via the single-flight controller (e.g. lose the user's
  // filter-change response). Set in the fetch effect, cleared on
  // successful completion (run's finally) and on effect cleanup.
  const inFlightModeRef = useRef<FetchTask["mode"] | null>(null);

  // useLayoutEffect (not useEffect) so refs are fresh before rAF callbacks
  // run. The fetch effect's release helper schedules a recheck via rAF that
  // reads logsRef/hasOlderRef/hasNewerRef — if these were synced via
  // useEffect (which fires after paint, after rAF), the recheck would see
  // stale state and re-fire the same fetch request as a duplicate.
  useLayoutEffect(() => {
    logsRef.current = logs;
  }, [logs]);

  useLayoutEffect(() => {
    hasOlderRef.current = hasOlder;
  }, [hasOlder]);

  useLayoutEffect(() => {
    hasNewerRef.current = hasNewer;
  }, [hasNewer]);

  useLayoutEffect(() => {
    fetchTaskRef.current = fetchTask;
  }, [fetchTask]);

  // After a logs update, either:
  //  (a) snap to the top — for live-tail prepends (WS-driven or scroll-
  //      back-to-top recovery), so the freshly arrived logs are visible
  //  (b) restore the previous scroll position — for both directions of
  //      "trim recovery" pagination (fetchNewer prepending at the top,
  //      fetchOlder appending at the bottom), so the user's view stays
  //      anchored to the log line they were reading
  // Cases are mutually exclusive: live-tail tasks skip the anchor
  // capture in the setLogs callback.
  //
  // For (b) we use DOM measurement rather than a scrollHeight delta.
  // Why: when we add N rows at one end and trim N rows from the other
  // in the same setLogs commit (to keep the buffer ≤ MAX_LOGS), the
  // total scrollHeight is unchanged — `delta = newH - oldH = 0` — so a
  // delta-based scrollTop adjustment leaves the user at the wrong end,
  // looking at the freshly fetched rows, which immediately re-triggers
  // the trigger zone and produces an infinite-loop fetch. Measuring the
  // anchor element's new viewport position bypasses the conflation
  // entirely.
  useLayoutEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    if (snapToTopRef.current) {
      container.scrollTop = 0;
      lastScrollTopRef.current = 0;
      isAtTopRef.current = true;
      snapToTopRef.current = false;
      anchorIdRef.current = null;

      return;
    }

    const anchorId = anchorIdRef.current;
    if (!anchorId) return;
    anchorIdRef.current = null;

    const el = container.querySelector(
      `[data-log-id="${CSS.escape(anchorId)}"]`,
    );
    if (!(el instanceof HTMLElement)) return;

    // The anchor element's viewport-relative position before the
    // setLogs commit is stored in anchorViewportTopRef. Compute its
    // *new* viewport-relative position and bump scrollTop by the
    // delta, so the element appears at the same place in the user's
    // viewport before and after the commit. This works equally well
    // for prepends (anchor = old top) and appends (anchor = old bottom).
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const newViewportTop = elRect.top - containerRect.top;
    const adjustment = newViewportTop - anchorViewportTopRef.current;
    container.scrollTop += adjustment;

    lastScrollTopRef.current = container.scrollTop;
  }, [logs]);

  // Helper: release the scroll-fetch gate. ALWAYS deferred via rAF so the
  // sequence is deterministic:
  //   1. setLogs queues an update
  //   2. React commits (sync)
  //   3. useLayoutEffect runs (logsRef sync, scroll restore for prepends)
  //   4. rAF callback runs → clear gate, recheck
  // The recheck reads logsRef + DOM measurements; both must reflect the
  // post-commit state, otherwise we'd re-fire the same fetch with a stale
  // cursor and create duplicate requests.
  const recheckTriggerZonesRef = useRef<() => void>(() => {});
  const releaseScrollGate = useCallback(() => {
    requestAnimationFrame(() => {
      scrollFetchInFlightRef.current = false;
      recheckTriggerZonesRef.current();
    });
  }, []);

  // Capture the viewport-relative position of a log line so the
  // scroll-restore layoutEffect can keep that line at the same place
  // in the user's viewport across the next setLogs commit. Used by
  // both the prepend (newer) and append (older) paths.
  const captureScrollAnchor = useCallback((id: string | null) => {
    if (!id || !scrollRef.current) {
      anchorIdRef.current = null;

      return;
    }
    const el = scrollRef.current.querySelector(
      `[data-log-id="${CSS.escape(id)}"]`,
    );
    if (!(el instanceof HTMLElement)) {
      anchorIdRef.current = null;

      return;
    }
    const containerRect = scrollRef.current.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    anchorIdRef.current = id;
    anchorViewportTopRef.current = elRect.top - containerRect.top;
  }, []);

  // Build a ransack query from a filter snapshot. Used by the fetch effect
  // so the request always matches the filters captured at task-creation time.
  // Composes the parsed search AST with the level filter at the AST level
  // (so OR / nested groups in the search compose correctly with levels).
  const buildRansack = useCallback(
    (filters: {
      levels: Level[];
      search: string;
    }): RansackQueryType | undefined => {
      let ast = parseSearchInput(filters.search);

      if (filters.levels.length > 0) {
        // Levels are an additional AND clause on top of the user's search.
        // Multi-level → emit one cond per level inside an OR group (since
        // ransack's _in predicate doesn't have a clean nested-group form).
        const levelNode: AstNode =
          filters.levels.length === 1
            ? {
                type: "cond",
                attr: "level",
                predicate: "eq",
                value: filters.levels[0],
              }
            : {
                type: "group",
                op: "or",
                children: filters.levels.map(
                  (l): Condition => ({
                    type: "cond",
                    attr: "level",
                    predicate: "eq",
                    value: l,
                  }),
                ),
              };

        ast = ast
          ? { type: "group", op: "and", children: [ast, levelNode] }
          : levelNode;
      }

      if (!ast) return undefined;

      return astToRansack(ast) as unknown as RansackQueryType;
    },
    [],
  );

  // Helper to schedule a new fetch task. Captures the current filter state
  // so the response can't be applied under a stale filter.
  const scheduleTask = useCallback(
    (task: Omit<FetchTask, "id" | "filters">) => {
      taskIdRef.current += 1;
      setFetchTask({
        ...task,
        id: taskIdRef.current,
        filters: {
          levels: Array.from(levelFilters),
          search: searchValue,
        },
      });
    },
    [levelFilters, searchValue],
  );

  // The single fetch effect. Whenever fetchTask changes, abort any prior
  // request and run the new one. AbortController guarantees true single-flight.
  useEffect(() => {
    if (!fetchTask) return;

    const controller = new AbortController();
    currentControllerRef.current = controller;
    inFlightModeRef.current = fetchTask.mode;
    setLoading(true);

    const run = async () => {
      const result = await tryCatch(
        apiClient.sites.proxyRequestsDetail(
          siteId,
          {
            after: fetchTask.after,
            before: fetchTask.before,
            q: buildRansack(fetchTask.filters),
          },
          { signal: controller.signal },
        ),
      );

      if (controller.signal.aborted) return;
      setLoading(false);

      if (result.error) {
        enqueueSnackbar(messageFromError(result.error), {
          variant: "error",
          persist: true,
        });
        releaseScrollGate();

        return;
      }

      const newLogs = result.data.data.proxy_requests;

      if (fetchTask.mode === "replace") {
        // A replace swaps the whole result set (filter/search change, WS
        // reconnect, fallback). Snap back to the top so the user lands on
        // the newest matching rows — the scroll-restore layout effect picks
        // this up on the setLogs commit. Without it the container keeps its
        // old scrollTop; on a short result that leaves the user in the
        // bottom trigger zone and fires an extra `older` fetch.
        snapToTopRef.current = true;
        setLogs(newLogs);
        setHasOlder(newLogs.length > 0);
        setHasNewer(false);
        releaseScrollGate();

        return;
      }

      if (fetchTask.mode === "older") {
        if (newLogs.length === 0) {
          setHasOlder(false);
          releaseScrollGate();

          return;
        }

        // Anchor: the bottom of the current buffer. After the append +
        // possible top-trim, the layout effect uses this to keep the
        // user's view of that log line stable.
        captureScrollAnchor(
          logsRef.current[logsRef.current.length - 1]?.id ?? null,
        );

        setLogs((prev) => {
          const fresh = dedupeAgainst(prev, newLogs);
          if (fresh.length === 0) {
            anchorIdRef.current = null;

            return prev;
          }

          const merged = [...prev, ...fresh];

          if (merged.length > MAX_LOGS) {
            setHasNewer(true);

            return merged.slice(-MAX_LOGS);
          }

          return merged;
        });
        releaseScrollGate();

        return;
      }

      // newer
      if (newLogs.length === 0) {
        if (fetchTask.before) setHasNewer(false);
        releaseScrollGate();

        return;
      }

      // Live-tail prepend (WS / scroll-back-to-top): the layout effect
      // will snap to scrollTop=0 once the new logs are committed. For
      // trim-recovery prepends (user scrolled up to recover history
      // that was trimmed off the top of the buffer) we anchor to the
      // current top log so the layout effect can find it after the
      // prepend and adjust scrollTop to keep the user's view stable.
      if (fetchTask.liveTail) {
        snapToTopRef.current = true;
      } else if (fetchTask.before) {
        captureScrollAnchor(logsRef.current[0]?.id ?? null);
      }

      setLogs((prev) => {
        const fresh = dedupeAgainst(prev, newLogs);
        if (fresh.length === 0) {
          anchorIdRef.current = null;
          snapToTopRef.current = false;

          return prev;
        }

        const merged = [...fresh, ...prev];

        if (merged.length > MAX_LOGS) {
          setHasOlder(true);

          return merged.slice(0, MAX_LOGS);
        }

        return merged;
      });
      releaseScrollGate();
    };

    void run().finally(() => {
      // Clear the in-flight mode marker on completion, but only if this
      // run wasn't superseded (aborted). When a new task has taken over,
      // the new effect has already set inFlightModeRef to its own mode
      // and we mustn't overwrite it.
      if (!controller.signal.aborted) {
        inFlightModeRef.current = null;
        // Drain any WS backlog that accumulated while this fetch was
        // in flight. No-op if nothing new arrived or the user scrolled
        // away mid-flight (flushDirty re-checks its gates).
        flushDirtyRef.current();
      }
    });

    return () => {
      controller.abort();
      inFlightModeRef.current = null;
      if (currentControllerRef.current === controller) {
        currentControllerRef.current = null;
      }
    };
  }, [fetchTask, siteId, buildRansack, releaseScrollGate, captureScrollAnchor]);

  // ── Filter changes → schedule a replace task ─────────────────
  // Skip the very first render (we wait for the WS connected handler or
  // the 3s fallback to schedule the initial fetch).
  const initialMountRef = useRef(true);
  useEffect(() => {
    if (initialMountRef.current) {
      initialMountRef.current = false;

      return;
    }
    scheduleTask({ mode: "replace" });
  }, [levelFilters, searchValue, scheduleTask]);

  // (scroll handler is set up below, after fetchOlder/fetchNewer are defined)

  // ── WS-driven refresh: dirty-flag model ─────────────────────
  //
  // The WS handler never fires a fetch directly. Instead it flips
  // isDirtyRef and calls flushDirty(). flushDirty schedules a `newer`
  // fetch only when ALL of these are true:
  //   - isDirtyRef is set
  //   - the user is parked at the top (isAtTopRef)
  //   - no fetch is in flight (inFlightModeRef === null)
  //   - there is at least one log in the buffer (otherwise the initial
  //     `replace` that the connected handler fires will cover it)
  //
  // flushDirty is invoked from three places:
  //   - the WS "new-logs" handler — try-now path
  //   - the fetch effect's run().finally — drain the backlog once an
  //     in-flight fetch settles (this handles the "filter-change was
  //     racing a WS signal" case — defer during, flush after)
  //   - the scroll handler — when the user scrolls back to the top
  //
  // Coalescing is implicit in the boolean flag (many WS events set it
  // the same way), and the natural rate limit is the fetch duration
  // itself: we can't start another `newer` until the current one has
  // landed and released the in-flight gate.
  const flushDirty = useCallback(() => {
    if (!isDirtyRef.current) return;
    if (!isAtTopRef.current) return;
    if (inFlightModeRef.current !== null) return;

    const current = logsRef.current;
    // Nothing to be "newer" than yet. The initial `replace` will
    // cover it; just hold the dirty flag until logs land.
    if (current.length === 0) return;

    isDirtyRef.current = false;
    scheduleTask({
      mode: "newer",
      before: current[0].id,
      liveTail: true,
    });
  }, [scheduleTask]);

  // Expose flushDirty via a ref so the fetch effect can call it from
  // its `finally` without taking flushDirty as a dep (which would make
  // the effect re-run on every filter change and abort the in-flight
  // fetch through the single-flight controller).
  const flushDirtyRef = useRef(flushDirty);
  flushDirtyRef.current = flushDirty;

  // ── WebSocket ───────────────────────────────────────────────
  //
  // Hold the live handler closures in a ref so cableHandlers can stay
  // referentially stable across renders. Without this, every change to
  // levelFilters / searchValue (which re-creates scheduleTask →
  // flushDirty → cableHandlers) would re-subscribe the channel — and
  // each `connected` callback fired by the new subscription kicks off
  // another `replace` fetch, which was the original duplicate-request
  // bug this indirection fixes.

  const wsHandlersRef = useRef({
    connected: () => {},
    disconnected: () => {},
    receivedNewLogs: () => {},
  });

  wsHandlersRef.current = {
    connected: () => {
      onConnectionChange?.(true);
      // Only pull the latest batch when the user is at the top of the
      // view. If they've scrolled down (investigating older entries),
      // mark the buffer dirty and wait — the scroll-back-to-top
      // handler will call flushDirty(). On the very first connect the
      // buffer is empty, so we always fetch.
      if (logsRef.current.length === 0 || isAtTopRef.current) {
        scheduleTask({ mode: "replace" });
      } else {
        isDirtyRef.current = true;
      }
    },
    disconnected: () => onConnectionChange?.(false),
    receivedNewLogs: () => {
      isDirtyRef.current = true;
      flushDirty();
    },
  };

  const cableHandlers = useMemo(
    () => ({
      connected: () => wsHandlersRef.current.connected(),
      disconnected: () => wsHandlersRef.current.disconnected(),
      received: (data: { action: string }) => {
        if (data.action === "new-logs") {
          wsHandlersRef.current.receivedNewLogs();
        }
      },
    }),
    [],
  );

  useCableSubscription("SiteLogsChannel", { id: siteId }, cableHandlers);

  // Fallback: if WS doesn't connect within 3s, load logs anyway.
  // Reads fetchTaskRef (synced from state) instead of `fetchTask` directly,
  // so the closure-captured value isn't stuck at the initial null. Without
  // this, the fallback fires unconditionally at t=3s — even when a task
  // is already in flight — and aborts the in-flight request via the
  // single-flight controller, doubling the wait time on slow backends.
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (logsRef.current.length === 0 && !fetchTaskRef.current) {
        scheduleTask({ mode: "replace" });
      }
    }, 3000);

    return () => clearTimeout(timeout);
    // Intentionally only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => onConnectionChange?.(false);
  }, [onConnectionChange]);

  // ── Infinite scroll (scroll-handler driven) ─────────────────
  //
  // We don't use IntersectionObserver here. The observer only fires on
  // intersection *transitions* (out → in), so once the user is inside the
  // trigger zone and a fetch lands them still inside it (small responses,
  // restored scroll near the edge), the observer is silent — they'd have to
  // scroll out of the zone and back in to re-trigger. The scroll handler
  // approach re-evaluates on every scroll event, which avoids the dead zone.

  const fetchOlder = useCallback(() => {
    if (scrollFetchInFlightRef.current) return;
    if (!hasOlderRef.current) return;
    const current = logsRef.current;
    const lastLog = current[current.length - 1];
    if (!lastLog) return;
    scrollFetchInFlightRef.current = true;
    scheduleTask({ mode: "older", after: lastLog.id });
  }, [scheduleTask]);

  const fetchNewer = useCallback(() => {
    if (scrollFetchInFlightRef.current) return;
    if (!hasNewerRef.current) return;
    const current = logsRef.current;
    const firstLog = current[0];
    if (!firstLog) return;
    scrollFetchInFlightRef.current = true;
    scheduleTask({ mode: "newer", before: firstLog.id });
  }, [scheduleTask]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const checkAndFetch = () => {
      const wasAtTop = isAtTopRef.current;
      const nowAtTop = el.scrollTop < AT_TOP_THRESHOLD;
      isAtTopRef.current = nowAtTop;

      // The user just scrolled back to the top. If the buffer is dirty
      // (a WS "new-logs" arrived while they were scrolled away, or a
      // fetch landed with the flag still set), flushDirty picks it up
      // and fires the deferred refresh.
      if (!wasAtTop && nowAtTop) {
        flushDirty();
      }

      // Top trigger zone → load newer (trim recovery)
      if (el.scrollTop < SCROLL_TRIGGER_MARGIN) {
        fetchNewer();
      }
      // Bottom trigger zone → load older
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distFromBottom < SCROLL_TRIGGER_MARGIN) {
        fetchOlder();
      }
    };

    const onScroll = () => {
      lastScrollTopRef.current = el.scrollTop;

      // If a fetch is in flight in one direction and the user has just
      // scrolled to the opposite end of the buffer, the in-flight
      // results are no longer relevant. Abort the request and clear
      // the gate so the next fetchNewer/fetchOlder can fire immediately
      // — without this the user has to wait for the unwanted fetch to
      // complete before the new one can start.
      if (currentControllerRef.current && fetchTaskRef.current) {
        const inFlightMode = fetchTaskRef.current.mode;
        const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        const movedToTop =
          inFlightMode === "older" && el.scrollTop < AT_TOP_THRESHOLD;
        const movedToBottom =
          inFlightMode === "newer" && distFromBottom < AT_TOP_THRESHOLD;

        if (movedToTop || movedToBottom) {
          currentControllerRef.current.abort();
          currentControllerRef.current = null;
          scrollFetchInFlightRef.current = false;
          // The aborted run() returns before reaching setLoading(false),
          // so clear it manually to make the spinner disappear right away.
          setLoading(false);
        }
      }

      checkAndFetch();
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    // Expose the trigger-zone check so the gate-release helper can re-fire
    // it after a fetch lands the user inside a zone with no fresh scroll
    // events to drive the next fetch.
    recheckTriggerZonesRef.current = checkAndFetch;
    // Initial check in case the content already fits within a trigger zone
    // (e.g. first response fits the viewport).
    checkAndFetch();

    return () => {
      el.removeEventListener("scroll", onScroll);
      recheckTriggerZonesRef.current = () => {};
    };
  }, [fetchOlder, fetchNewer, flushDirty]);

  // ── Actions ────────────────────────────────────────────────

  // handleLevelFilter / level filter UI is intentionally disabled —
  // every proxy log entry is recorded at "info" level today, so the
  // pills wouldn't change the result set. Re-enable when warn/error
  // logs ship. The `levelFilters` state is kept so the filter snapshot
  // captured in scheduleTask still has the right shape.

  const commitSearch = (value: string) => {
    setSearchValue(value);
  };

  const handleClearSearch = () => {
    setSearchDraft("");
    if (searchValue !== "") commitSearch("");
    searchInputRef.current?.focus();
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (searchDraft !== searchValue) commitSearch(searchDraft);
    }
  };

  // Insert "token:" at the cursor position (or end), then focus the input
  const handleInsertField = (token: string) => {
    setSearchFieldOpen(false);
    const input = searchInputRef.current;
    const insert = `${token}:`;

    const isFocused = input && document.activeElement === input;
    const start = isFocused
      ? (input.selectionStart ?? searchDraft.length)
      : searchDraft.length;
    const end = isFocused
      ? (input.selectionEnd ?? searchDraft.length)
      : searchDraft.length;

    const before = searchDraft.slice(0, start);
    const after = searchDraft.slice(end);
    const sep = before && !before.endsWith(" ") ? " " : "";
    const fragment = `${sep}${insert}`;
    const next = before + fragment + after;

    setSearchDraft(next);

    requestAnimationFrame(() => {
      if (!input) return;
      input.focus();
      const cursor = (before + fragment).length;
      input.setSelectionRange(cursor, cursor);
    });
  };

  const handleTokenFilter = (token: string, value: string) => {
    const next = upsertSearchToken(searchDraft, token, value);
    setSearchDraft(next);
    commitSearch(next);
  };

  // Close search field dropdown when clicking outside
  useEffect(() => {
    if (!searchFieldOpen) return;

    const handler = (e: MouseEvent) => {
      if (
        searchFieldDropdownRef.current &&
        !searchFieldDropdownRef.current.contains(e.target as Node)
      ) {
        setSearchFieldOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);

    return () => document.removeEventListener("mousedown", handler);
  }, [searchFieldOpen]);

  // Close search help popover when clicking outside
  useEffect(() => {
    if (!searchHelpOpen) return;

    const handler = (e: MouseEvent) => {
      if (
        searchHelpRef.current &&
        !searchHelpRef.current.contains(e.target as Node)
      ) {
        setSearchHelpOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);

    return () => document.removeEventListener("mousedown", handler);
  }, [searchHelpOpen]);

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <span className={styles.toolbarTitle}>Request Logs</span>
        {/*
          Level filters are hidden for now: every proxy log entry is
          recorded at "info" level, so the filter pills wouldn't change
          the result set. Re-enable when warn/error logs ship.
        */}
      </div>

      <div className={styles.logScroll} ref={scrollRef}>
        <div ref={topSentinelRef} className={styles.scrollSentinel} />

        {logs.map((log) => (
          // data-log-id lets the scroll-restore layoutEffect find this
          // wrapper after a "newer" prepend and measure how much height
          // was added above it (without relying on scrollHeight deltas,
          // which break when we trim from the bottom in the same commit).
          <div key={log.id} data-log-id={log.id}>
            <button
              className={clsx(
                styles.logLine,
                expandedId === log.id && styles.logLineExpanded,
              )}
              onClick={() =>
                setExpandedId(expandedId === log.id ? null : log.id)
              }
            >
              <span className={styles.logLineToggle} aria-hidden>
                {expandedId === log.id ? "−" : "+"}
              </span>
              <LogTokens log={log} onFilter={handleTokenFilter} />
            </button>
            {expandedId === log.id && (
              <div className={styles.detailPanel}>
                <LogDetail
                  log={log}
                  onFilter={handleTokenFilter}
                  crsRulesById={crsRulesById}
                  onCreateException={(seed) => setDirectiveSeed(seed)}
                />
              </div>
            )}
          </div>
        ))}

        {!loading && logs.length === 0 && (
          <div className={styles.emptyState}>No logs found.</div>
        )}

        <div ref={bottomSentinelRef} className={styles.scrollSentinel} />
      </div>

      <div className={styles.footer}>
        <div className={styles.searchGroup}>
          <div className={styles.searchHelpWrapper} ref={searchHelpRef}>
            <button
              type="button"
              className={styles.searchHelpButton}
              onClick={() => setSearchHelpOpen((o) => !o)}
              title="Search syntax help"
              aria-label="Search syntax help"
            >
              <Icon iconName="InfoIcon" width="1rem" height="1rem" />
            </button>
            {searchHelpOpen && (
              <div className={styles.searchHelpPopover}>
                <div className={styles.searchHelpTitle}>Search syntax</div>
                <div className={styles.searchHelpBody}>
                  <p>
                    Combine <code>key:value</code> filters with spaces. ANDed by
                    default.
                  </p>
                  <pre>ip:198.49.222.20 status:500 method:GET</pre>

                  <div className={styles.searchHelpSection}>
                    AND / OR / NOT / groups
                  </div>
                  <p>
                    Use <code>or</code> for OR and <code>not</code> for NOT.
                    Group with parens. NOT binds tightest, then AND, then OR.
                  </p>
                  <ul>
                    <li>
                      <code>status:500..599 (rule:0.. or directive:0..)</code> —
                      server errors that also triggered any WAF rule or
                      directive
                    </li>
                    <li>
                      <code>(status:404 or status:410) ip:1.2.3.4</code> — 404
                      or 410 from a specific IP
                    </li>
                    <li>
                      <code>method:POST or method:PUT</code> — writes only
                    </li>
                    <li>
                      <code>status:500 not uri:/api/*</code> — 500s that
                      aren&apos;t under <code>/api/</code>
                    </li>
                    <li>
                      <code>not (uri:*.css or uri:*.js)</code> — exclude all
                      static assets
                    </li>
                  </ul>

                  <div className={styles.searchHelpSection}>Wildcards</div>
                  <p>
                    Use <code>*</code> to match a substring:
                  </p>
                  <ul>
                    <li>
                      <code>uri:/api/*</code> — starts with <code>/api/</code>
                    </li>
                    <li>
                      <code>uri:*.json</code> — ends with <code>.json</code>
                    </li>
                    <li>
                      <code>uri:*/users/*</code> — contains <code>/users/</code>
                    </li>
                  </ul>

                  <div className={styles.searchHelpSection}>Numeric ranges</div>
                  <p>
                    Integer fields like <code>status</code> use range or exact
                    match instead of wildcards.
                  </p>
                  <ul>
                    <li>
                      <code>status:400..499</code> — any value in the inclusive
                      range
                    </li>
                    <li>
                      <code>status:500..</code> — open-ended (≥ 500)
                    </li>
                    <li>
                      <code>status:..399</code> — open-ended (≤ 399)
                    </li>
                    <li>
                      <code>status:500</code> — exact match
                    </li>
                    <li>
                      <code>rule:0..</code> — has any value (e.g. records that
                      hit a WAF rule)
                    </li>
                  </ul>

                  <div className={styles.searchHelpSection}>Date range</div>
                  <p>
                    Use <code>from:</code> and <code>to:</code>. Most common
                    formats are accepted — ISO, US (M/D/YYYY), with or without
                    time. Times are interpreted as UTC (matching the log
                    timestamps); add a <code>Z</code> or offset to be explicit.
                  </p>
                  <pre>from:2026-04-09 to:2026-04-09T23:59:59</pre>
                  <pre>from:4/9/2026 to:&quot;4/9/2026 11:59 PM&quot;</pre>

                  <div className={styles.searchHelpSection}>Quoted values</div>
                  <p>
                    Wrap values in <code>&quot;...&quot;</code> for spaces,
                    colons, or quotes. Escape inner quotes with{" "}
                    <code>\&quot;</code>.
                  </p>
                  <pre>
                    {
                      'error:"upstream: Get \\"https://example.com/\\": connection reset"'
                    }
                  </pre>

                  <div className={styles.searchHelpSection}>Tips</div>
                  <ul>
                    <li>
                      Click any value in a log line or expanded entry to
                      add/replace its filter.
                    </li>
                    <li>Press Enter to apply the search.</li>
                    <li>
                      Use the <strong>Add filter</strong> dropdown to insert a
                      key prefix.
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
          <div
            className={styles.searchFieldDropdown}
            ref={searchFieldDropdownRef}
          >
            <button
              type="button"
              className={styles.searchFieldButton}
              onClick={() => setSearchFieldOpen((o) => !o)}
              disabled={loading}
            >
              Add filter
              <span className={styles.searchFieldChevron}>▾</span>
            </button>
            {searchFieldOpen && (
              <div className={styles.searchFieldMenu}>
                {(() => {
                  const ungrouped = SEARCH_FIELDS.filter((f) => !f.group);
                  const groupedByName = SEARCH_FIELDS.reduce<
                    Record<string, SearchFieldDef[]>
                  >((acc, f) => {
                    if (!f.group) return acc;
                    (acc[f.group] ||= []).push(f);

                    return acc;
                  }, {});

                  const renderItem = (field: SearchFieldDef) => (
                    <button
                      key={field.token}
                      type="button"
                      className={styles.searchFieldMenuItem}
                      onClick={() => handleInsertField(field.token)}
                    >
                      <span>{field.label}</span>
                      <span className={styles.searchFieldMenuItemHint}>
                        {field.token}:
                      </span>
                    </button>
                  );

                  return (
                    <>
                      {ungrouped.map(renderItem)}
                      {Object.entries(groupedByName).map(
                        ([groupName, fields]) => (
                          <div
                            key={groupName}
                            className={styles.searchFieldMenuGroup}
                          >
                            <div className={styles.searchFieldMenuGroupHeader}>
                              {groupName}
                            </div>
                            {fields.map(renderItem)}
                          </div>
                        ),
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
          <input
            ref={searchInputRef}
            type="text"
            className={clsx(
              styles.searchInput,
              // Animated stripe overlay while a user-driven fetch is in
              // flight (replace / older / newer trim-recovery). Excluded
              // for live-tail prepends so the WS feed doesn't constantly
              // flash the indicator.
              loading && !fetchTask?.liveTail && styles.searchInputLoading,
            )}
            placeholder="uri:/pages/* status:500..599 (rule:0.. or directive:0..)   (combine with AND / OR / parens)"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            spellCheck={false}
            autoComplete="off"
          />
          {(searchDraft || searchValue) && (
            <button
              type="button"
              className={styles.searchClearButton}
              onClick={handleClearSearch}
              title="Clear search"
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {directiveSeed !== null && (
        <WafDirectiveModal
          siteId={siteId}
          directive={null}
          availableRules={crsRules}
          availableGroupIds={crsGroupIds}
          onClose={() => setDirectiveSeed(null)}
          queryKey={queryKeys.wafDirectives(siteId)}
          seed={directiveSeed}
        />
      )}
    </div>
  );
};

// ── Detail Panel ─────────────────────────────────────────────

// Detail fields, grouped into explicit rows. Each row renders as a grid
// with N equal columns where N = number of *visible* fields in that row
// (after null/empty filtering). Single-field rows naturally collapse to
// one column = full width. The synthetic "_time" key isn't a ProxyRequest
// field — it's the formatted log.created_at, handled inline below.
type DetailFieldKey = keyof ProxyRequest | "_time";

const DETAIL_ROWS: DetailFieldKey[][] = [
  ["_time", "client_ip", "verb", "port", "status", "duration"],
  ["region", "bot", "guard_action", "guard_serial", "cache", "trace"],
  ["waf_directive", "waf_rule", "waf_action", "waf_level", "waf_phase"],
  ["uri"],
  ["ua"],
  ["err"],
];

// Uniform column width across all multi-field rows so visible cells in
// shorter rows (or rows with missing values) align under the equivalent
// columns above. Single-field rows are treated as full-width.
const DETAIL_GRID_COLUMNS = Math.max(
  ...DETAIL_ROWS.filter((r) => r.length > 1).map((r) => r.length),
);

const DETAIL_LABELS: Partial<Record<DetailFieldKey, string>> = {
  _time: "Time",
  client_ip: "Client IP",
  verb: "Method",
  port: "Port",
  status: "Status",
  duration: "Duration",
  region: "Region",
  bot: "Bot",
  guard_action: "Guard Action",
  guard_serial: "Guard Serial",
  cache: "Cache",
  trace: "Trace",
  waf_directive: "WAF Directive",
  waf_rule: "WAF Rule",
  waf_action: "WAF Action",
  waf_level: "WAF Level",
  waf_phase: "WAF Phase",
  uri: "URI",
  ua: "User Agent",
  err: "Error",
};

type LogDetailProps = {
  log: ProxyRequest;
  onFilter: (token: string, value: string) => void;
  crsRulesById: Map<number, CrsRule>;
  onCreateException: (seed: DirectiveSeed) => void;
};

const FILTER_TOKEN_BY_DETAIL_KEY: Partial<Record<DetailFieldKey, string>> = {
  // Clicking a timestamp filters to records up to and including that moment
  // (`to:` / created_at_lteq). With the newest-at-top ordering this puts the
  // clicked record at the top of the result and older records below it —
  // `from:` would instead hide everything older than the row you clicked.
  _time: "to",
  client_ip: "ip",
  port: "port",
  region: "region",
  verb: "method",
  status: "status",
  bot: "bot",
  cache: "cache",
  waf_action: "waf",
  waf_rule: "rule",
  waf_directive: "directive",
  guard_action: "guard",
  guard_serial: "serial",
  trace: "ref",
  uri: "uri",
  err: "error",
};

// Strip fractional seconds from an ISO 8601 timestamp:
//   "2026-04-09T19:04:18.084Z"      → "2026-04-09T19:04:18Z"
//   "2026-04-09T19:04:18.084123456" → "2026-04-09T19:04:18"
function truncateTimestamp(iso: string): string {
  return iso.replace(/\.\d+/, "");
}

const LogDetail = ({
  log,
  onFilter,
  crsRulesById,
  onCreateException,
}: LogDetailProps) => {
  const handleClick = (e: React.MouseEvent, token: string, value: string) => {
    e.stopPropagation();
    onFilter(token, value);
  };

  const timestamp = truncateTimestamp(log.created_at);

  // Resolve a row key into the rendered cell (or null if empty / missing).
  // Centralised here so the per-row map below stays compact and the time
  // field's special-case (synthetic key, pre-formatted value) lives next
  // to the regular fields it visually sits beside.
  const renderField = (key: DetailFieldKey) => {
    const label = DETAIL_LABELS[key];
    let display: string;

    if (key === "_time") {
      display = timestamp;
    } else {
      const value = log[key];
      if (value == null || value === "") return null;
      display =
        key === "duration" && typeof value === "number"
          ? formatDuration(value)
          : String(value);
    }

    const filterToken = FILTER_TOKEN_BY_DETAIL_KEY[key];
    const clickable = filterToken !== undefined;
    const titleLabel =
      key === "_time" ? "Filter up to this time" : `Filter by ${filterToken}`;

    // The time cell is displayed truncated to seconds for readability, but
    // the filter must use the full-precision timestamp — otherwise the `to:`
    // (lteq) bound rounds below the record's real sub-second time and the
    // row you clicked drops out of its own result set.
    const filterValue = key === "_time" ? log.created_at : display;

    return (
      <div key={key} className={styles.detailItem}>
        <span className={styles.detailLabel}>{label}</span>
        <span
          role={clickable ? "button" : undefined}
          tabIndex={clickable ? 0 : undefined}
          className={clsx(
            styles.detailValue,
            key === "err" && styles.detailError,
            clickable && styles.tokenClickable,
          )}
          onClick={
            clickable && filterToken
              ? (e) => handleClick(e, filterToken, filterValue)
              : undefined
          }
          title={clickable ? titleLabel : undefined}
        >
          {display}
        </span>
      </div>
    );
  };

  return (
    <div className={styles.detailGrid}>
      {DETAIL_ROWS.map((rowKeys, rowIdx) => {
        const cells = rowKeys
          .map(renderField)
          .filter((c): c is ReactElement => c !== null);
        if (cells.length === 0) return null;

        // Single-field rows (URI, User Agent, Error) collapse to one
        // full-width column. Multi-field rows always render with the
        // grid's full column count so visible cells pack to the left
        // and align with the columns of the row above, even when some
        // fields in this row are missing on this particular log.
        const cols = rowKeys.length === 1 ? 1 : DETAIL_GRID_COLUMNS;

        return (
          <div
            key={rowIdx}
            className={styles.detailRow}
            style={{
              gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
            }}
          >
            {cells}
          </div>
        );
      })}

      {log.waf_match && log.waf_match.length > 0 && (
        <div className={styles.detailItem}>
          <span className={styles.detailLabel}>WAF Matches</span>
          <div className={styles.wafMatchTable}>
            {log.waf_match.map((match, idx) => (
              <WafMatchEntry
                key={`${match.rid ?? idx}-${idx}`}
                match={match}
                log={log}
                rule={
                  match.rid != null ? crsRulesById.get(match.rid) : undefined
                }
                onFilter={handleClick}
                onCreateException={onCreateException}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── WAF match entry ──────────────────────────────────────────

type WafMatch = NonNullable<ProxyRequest["waf_match"]>[number];

type WafMatchEntryProps = {
  match: WafMatch;
  log: ProxyRequest;
  rule: CrsRule | undefined;
  onFilter: (e: React.MouseEvent, token: string, value: string) => void;
  onCreateException: (seed: DirectiveSeed) => void;
};

const WafMatchEntry = ({
  match,
  log,
  rule,
  onFilter,
  onCreateException,
}: WafMatchEntryProps) => {
  const [dataOpen, setDataOpen] = useState(false);
  const hasMatches = !!match.matches && match.matches.length > 0;
  const ruleMessage = rule?.message?.trim();

  const handleAddException = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (match.rid == null) return;

    onCreateException({
      rules: [match.rid],
      method: log.verb ?? undefined,
      path: log.uri ?? undefined,
    });
  };

  return (
    <div className={styles.wafMatchEntry}>
      <div className={styles.wafMatchHeader}>
        {match.rid != null && (
          <span
            role="button"
            tabIndex={0}
            className={clsx(styles.wafMatchRule, styles.tokenClickable)}
            onClick={(e) => onFilter(e, "rule", String(match.rid))}
            title="Filter by rule"
          >
            Rule {match.rid}
          </span>
        )}
        {ruleMessage && (
          // CSS-driven tooltip: the popover is positioned by .wafMatchInfo
          // styles via :hover / :focus-within. The native `title` is a
          // belt-and-braces fallback for assistive tech / touch devices.
          <span
            className={styles.wafMatchInfo}
            tabIndex={0}
            role="img"
            aria-label={`Rule description: ${ruleMessage}`}
            title={ruleMessage}
          >
            ?<span className={styles.wafMatchInfoTooltip}>{ruleMessage}</span>
          </span>
        )}
        {match.rid != null && (
          <button
            type="button"
            className={styles.wafMatchExceptionBtn}
            onClick={handleAddException}
            title="Open the directive editor pre-filled with this rule, method, and path"
          >
            Add exception
          </button>
        )}
      </div>

      {hasMatches && (
        <>
          <button
            type="button"
            className={styles.wafMatchDataToggle}
            onClick={(e) => {
              e.stopPropagation();
              setDataOpen((o) => !o);
            }}
            aria-expanded={dataOpen}
          >
            <span className={styles.wafMatchDataToggleChevron} aria-hidden>
              {dataOpen ? "▾" : "▸"}
            </span>
            Matched data
          </button>
          {dataOpen && (
            <div className={styles.wafMatchVars}>
              {match.matches!.map((m, mIdx) => (
                <div key={mIdx} className={styles.wafMatchVar}>
                  <span className={styles.wafMatchVarKey}>
                    {m.Variable}
                    {m.Key ? `:${m.Key}` : ""}
                  </span>
                  {m.Value && (
                    <span className={styles.wafMatchVarValue}>
                      {" = "}
                      {m.Value}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RequestLog;
